import {
  EndpointExpectedResponseSchema,
  EndpointExpectedResponseVerbSchema,
} from './endpoints.schema';
import type { z } from 'zod';

export type EndpointExpectedResponse = z.infer<
  typeof EndpointExpectedResponseSchema
>;

type ExpectedResponseVerb = z.infer<typeof EndpointExpectedResponseVerbSchema>;

const STATE_CHANGING_VERBS = new Set<ExpectedResponseVerb>([
  'CREATE',
  'DELETE',
  'UPDATE',
]);

export type SanitizeExpectedResponsesResult = {
  sanitized: EndpointExpectedResponse[];
  droppedCount: number;
};

type RawExpectedResponse = {
  responseCode?: unknown;
  indicator?: unknown;
  verb?: unknown;
  entity?: unknown;
  description?: unknown;
  isStateChanging?: unknown;
};

const isRecord = (value: unknown): value is RawExpectedResponse =>
  typeof value === 'object' && value !== null;

const normalizeVerb = (verb: unknown): ExpectedResponseVerb | null => {
  const parsed = EndpointExpectedResponseVerbSchema.safeParse(verb);
  return parsed.success ? parsed.data : null;
};

const normalizeIsStateChanging = ({
  verb,
  isStateChanging,
}: {
  verb: ExpectedResponseVerb;
  isStateChanging: boolean;
}): boolean => {
  if (verb === 'NONE') {
    return false;
  }

  if (STATE_CHANGING_VERBS.has(verb)) {
    return true;
  }

  return isStateChanging;
};

export const sanitizeExpectedResponses = (
  responses: unknown,
): SanitizeExpectedResponsesResult => {
  if (!Array.isArray(responses)) {
    return { sanitized: [], droppedCount: 0 };
  }

  let droppedCount = 0;
  const sanitized: EndpointExpectedResponse[] = [];

  for (const response of responses) {
    if (!isRecord(response)) {
      droppedCount += 1;
      continue;
    }

    const verb = normalizeVerb(response.verb);
    if (!verb) {
      droppedCount += 1;
      continue;
    }

    const parsed = EndpointExpectedResponseSchema.safeParse({
      responseCode: response.responseCode,
      indicator: response.indicator,
      verb,
      entity: response.entity,
      description: response.description,
      isStateChanging: normalizeIsStateChanging({
        verb,
        isStateChanging: response.isStateChanging === true,
      }),
    });

    if (!parsed.success) {
      droppedCount += 1;
      continue;
    }

    sanitized.push(parsed.data);
  }

  return { sanitized, droppedCount };
};

export const expectedResponsesUpdatePatch = (
  expectedResponses: EndpointExpectedResponse[],
): { expectedResponses: EndpointExpectedResponse[] } => ({
  expectedResponses,
});
